import React, { useContext, useState, DragEvent } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IFileData, TFileExtension } from 'types';
import { postMediaFile } from 'backend/additionalInformation';
import { openBrowseFile } from 'utils/openBrowseFile';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';
import classNames from 'classnames';

interface IProps {
	hint: string;
	accept: TFileExtension[];
	maxFileSizeMB: number;
	testId: string;
	onUpload: (fileData: IFileData) => void;
	onSizeExceeded: () => void;
	onInvalidFormat: () => void;
	onUploadError: () => void;
	onTooManyFiles: () => void;
}

export const DragAndDropForFileNew: React.FC<IProps> = ({
	hint,
	accept,
	maxFileSizeMB,
	testId,
	onUpload,
	onSizeExceeded,
	onInvalidFormat,
	onUploadError,
	onTooManyFiles,
}) => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [isDragOver, setIsDragOver] = useState(false);
	const containerClasses = classNames(
		'p-4 border border-dashed border-purple-07-new rounded flex flex-col items-center gap-2 cursor-pointer',
		isDragOver ? 'bg-purple-01-new' : 'bg-white-00-new hover:bg-purple-01-new',
	);

	const uploadFile = async (files: FileList | null) => {
		const file = files?.[0];

		if (!file) {
			return;
		}

		const fileExtension = file.name.split('.').pop()?.toLowerCase();

		if (!accept.some((extension) => extension === fileExtension)) {
			onInvalidFormat();
			return;
		}

		if (file.size > 1000 * 1000 * maxFileSizeMB) {
			onSizeExceeded();
			return;
		}

		const formData = new FormData();
		formData.append('file', file, file.name);

		try {
			showLoader();
			const { data } = await postMediaFile({ data: formData });
			onUpload({ fileId: data.fileId, fileHash: data.fileHash, fileName: file.name });
		} catch {
			onUploadError();
		} finally {
			hideLoader();
		}
	};

	const handleClick = () => {
		openBrowseFile({ onChoose: uploadFile, accept: accept.map((extension) => `.${extension}`).join(', ') });
	};

	const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragOver(true);
	};

	const handleDragLeave = () => {
		setIsDragOver(false);
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragOver(false);

		if (e.dataTransfer.files.length > 1) {
			onTooManyFiles();
			return;
		}

		uploadFile(e.dataTransfer.files);
	};

	return (
		<div
			className={containerClasses}
			data-test={testId}
			onClick={handleClick}
			onDragEnter={handleDragOver}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}>
			<Icon name={IconName.uploadOutlined} size="xxl" className="pointer-events-none text-purple-07-new" />
			<div className="pointer-events-none">
				<div className="text-center">
					<Text
						translationNameSpace="form"
						translationKey="clickToUpload"
						theme="secondary"
						weight="bold"
						display="inline"
					/>{' '}
					<Text translationNameSpace="form" translationKey="orDragAndDrop" weight="bold" display="inline" />
				</div>
				<Text
					translationNameSpace="form"
					translationKey={hint}
					size="xs"
					theme="neutralMedium"
					align="center"
				/>
			</div>
		</div>
	);
};
